import AppConfig from '@/config';
import { getTraceId } from '@/lib/logger';
import { io, Socket } from 'socket.io-client';

type TokenGetter = () => Promise<string | null>;

class SocketClient {
  private socket: Socket | null = null;
  private isConnected: boolean = false;
  private accessToken: string | null = null;
  private tokenGetter: TokenGetter | null = null;
  private isConnecting: boolean = false; // Add a flag to indicate connection attempt

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  setTokenGetter(getter: TokenGetter) {
    this.tokenGetter = getter;
  }

  /**
   * Refresh the access token using the token getter (Clerk's getToken).
   * Returns the fresh token, or falls back to the current one.
   */
  private async refreshToken(): Promise<string | null> {
    if (this.tokenGetter) {
      try {
        const freshToken = await this.tokenGetter();
        if (freshToken) {
          this.accessToken = freshToken;
        }
        return this.accessToken;
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('Failed to refresh access token for socket:', e);
        return this.accessToken;
      }
    }
    return this.accessToken;
  }

  private connect() {
    if (!this.accessToken) {
      // eslint-disable-next-line no-console
      console.error('Authorization token is not set.');
      return;
    }

    if (this.socket) {
      // eslint-disable-next-line no-console
      console.warn('Socket is already connected.');
      return; // Prevent multiple connections
    }

    this.isConnecting = true; // Set the flag to indicate connection attempt

    // Note: trace_id is included per-message (in emit payloads as 'trace_id' field), not at connection time.
    // This allows each socket message to have its own trace_id for proper flow tracking.
    // The backend extracts trace_id from the message payload, not from headers.
    this.socket = io(`${AppConfig.endpoints.socket.base}`, {
      extraHeaders: {
        Authorization: `Bearer ${this.accessToken}`,
      },
      reconnection: true, // Enable reconnection
      reconnectionAttempts: 5, // Number of reconnection attempts
      reconnectionDelay: 1000, // Delay between reconnection attempts
    });

    // Refresh the token before each reconnection attempt so we don't send an expired JWT
    this.socket.io.on('reconnect_attempt', async () => {
      // eslint-disable-next-line no-console
      console.log('Socket.IO reconnecting — refreshing token...');
      const freshToken = await this.refreshToken();
      if (freshToken && this.socket) {
        this.socket.io.opts.extraHeaders = {
          ...this.socket.io.opts.extraHeaders,
          Authorization: `Bearer ${freshToken}`,
        };
      }
    });

    this.socket.on('connect', () => {
      // eslint-disable-next-line no-console
      console.log('Socket.IO connection opened.');
      this.isConnected = true;
      this.isConnecting = false; // Reset the flag on successful connection
    });

    this.socket.on('disconnect', (reason) => {
      // eslint-disable-next-line no-console
      console.log('Socket.IO connection closed:', reason);
      this.isConnected = false;
    });

    this.socket.on('error', (error) => {
      // eslint-disable-next-line no-console
      console.error('Socket.IO error:', error);
      this.isConnecting = false; // Reset the flag on error
    });

    // Handle server-side auth errors (e.g. expired token that slipped through).
    // Force a full reconnect with a fresh token.
    this.socket.on('auth_err', async () => {
      // eslint-disable-next-line no-console
      console.warn('Socket.IO auth error received — reconnecting with fresh token...');
      await this.reconnectWithFreshToken();
    });
  }

  /**
   * Tear down the current socket and reconnect with a fresh token.
   */
  private async reconnectWithFreshToken() {
    this.disconnect();
    await this.refreshToken();
    this.connect();
  }

  /**
   * Disconnect and clean up the socket. Call this when the org changes
   * so the next getSocket()/ensureConnected() creates a fresh connection
   * with the new org's token.
   */
  disconnect() {
    if (this.socket) {
      this.socket.removeAllListeners();
      this.socket.disconnect();
      this.socket = null;
    }
    this.isConnected = false;
    this.isConnecting = false;
    this.accessToken = null;
    this.tokenGetter = null;
  }

  async sendMessage(
    orgId: string,
    leadId: string,
    message: string,
    audioS3Key?: string,
    clientMessageId?: string
  ) {
    await this.ensureConnected(); // Ensure the socket is connected before sending a message
    if (this.socket) {
      // Include trace_id in the message payload for distributed tracing
      this.socket.emit('send_message', {
        orgId,
        messageDetails: {
          leadId,
          message,
          ...(audioS3Key && { audioS3Key }),
          ...(clientMessageId && { clientMessageId }),
        },
        trace_id: getTraceId(), // Each message gets current trace_id
      });
    } else {
      // eslint-disable-next-line no-console
      console.error('Socket.IO is not connected. Unable to send message to lead room.');
    }
  }

  async getSocket() {
    await this.ensureConnected(); // Ensure the socket is connected before returning it
    return this.socket;
  }

  isSocketConnected() {
    return this.isConnected;
  }

  private ensureConnected(): Promise<void> {
    return new Promise((resolve) => {
      if (this.isConnected) {
        resolve();
      } else if (this.isConnecting) {
        const checkConnection = setInterval(() => {
          if (this.isConnected) {
            clearInterval(checkConnection);
            resolve();
          }
        }, 100); // Check every 100ms
      } else {
        this.connect();
        const checkConnection = setInterval(() => {
          if (this.isConnected) {
            clearInterval(checkConnection);
            resolve();
          }
        }, 100); // Check every 100ms
      }
    });
  }
}

const socketClient = new SocketClient();
export default socketClient;
